








































import { computed, defineComponent, ref } from "@vue/composition-api";
import { StoryPublisher } from "@scrile/api-provider/dist/api/StoriesProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import useAuthUser from "@/hooks/useAuthUser";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { userPublicDataFieldsWithStoryInfo } from "@/lib/fieldsHelper";
import { $t } from "@/lib/i18nHelpers";
import { getLiveUrl, getProfileUrl } from "@/lib/urlHelpers";
import config from "@/config";
import { ViewStoriesSettings } from "@/types";
import { EVENTS } from "@/const";
import BaseAvatar from "@/components/BaseAvatar";
import BaseButton from "@/components/BaseButton";
import BaseConfirm from "@/components/BaseConfirm";
import BaseMenu from "@/components/BaseMenu";
import CustomScroll from "@/components/CustomScroll";

export interface ButtonConfig {
  name: string;
  class: string;
  title: string;
  condition: boolean;
  event: () => void;
}

export default defineComponent({
  name: "StoriesLine",
  components: { BaseButton, BaseConfirm, BaseMenu, CustomScroll, BaseAvatar },
  setup(_, { root }) {
    const { authUser, isCreator } = useAuthUser();
    const loading = ref(false);
    const authUserPublicData = ref<UserPublicData>();
    const showConfirm = ref(false);
    const selectedIndex = ref(-1);
    const storyPublishers = ref<StoryPublisher[]>([]);
    const enabledStories = computed(() => config.featureStories && !!storyPublishers.value.length);

    const getAuthUserPublicData = async () => {
      if (authUser.value && isCreator.value) {
        const resp = await providers.UserPublicDataProvider.search(
          { data: { id: [authUser.value.id] } },
          userPublicDataFieldsWithStoryInfo()
        );
        authUserPublicData.value = resp.result[0];
      }
    };

    const getStoryPublishers = async () => {
      const resp = await providers.StoriesProvider.searchStoryPublisher({
        data: {
          onlyFollowings: true,
          page: 0,
          size: 1000,
          sort: "STORY_VIEWED;LAST_STORY_CREATE_TIME,DESC",
        },
      });
      storyPublishers.value = resp.result;
    };

    const addingMyUser = () => {
      if (authUserPublicData.value) {
        const myId = storyPublishers.value.findIndex(item => item?.userId === authUserPublicData.value?.id);
        if (myId > -1) storyPublishers.value.splice(myId, 1);
        storyPublishers.value.unshift({ user: authUserPublicData.value, userId: authUserPublicData.value?.id });
      }
    };

    const getUserUrl = (user: UserPublicData) => getProfileUrl(user);
    const goLive = (user: UserPublicData) => getLiveUrl(user);
    const showStories = (story: StoryPublisher) => {
      selectedIndex.value = -1;
      return (
        config.featureStories &&
        emitter.emit<ViewStoriesSettings>(EVENTS.STORIES_SHOW, {
          storyPublishers: storyPublishers.value.filter(item => item.user.hasStories),
          currentUserId: story.userId,
          ...(story.user.firstNotViewedStoryId ? { currentStoryId: story.user.firstNotViewedStoryId } : {}),
          cb: async () => {
            await Promise.all([getStoryPublishers(), getAuthUserPublicData()]);
            addingMyUser();
          },
        })
      );
    };

    const isMeAndCanCreateStory = (userId: string) => {
      return authUser.value?.id === userId && isCreator.value;
    };

    const selectUser = (index: number) => {
      if (isMeAndCanCreateStory(storyPublishers.value[index].userId)) {
        if (storyPublishers.value[index].user.hasStories) {
          showStories(storyPublishers.value[index]);
        } else {
          emitter.emit(EVENTS.SHOW_CREATE_POST, { userId: storyPublishers.value[index].userId, type: "story" });
        }
      } else {
        selectedIndex.value = index;
      }
    };

    const buttons = computed<ButtonConfig[]>(() => [
      {
        name: "live",
        class: "__go-live",
        title: $t("Join live"),
        condition:
          selectedIndex.value > -1
            ? !!storyPublishers.value[selectedIndex.value].user.publicLivechatUser &&
              !(config.featureInvisibleMode && storyPublishers.value[selectedIndex.value].user.onlineStatus?.invisible)
            : false,
        event: () => {
          root.$router.push(goLive(storyPublishers.value[selectedIndex.value].user));
        },
      },
      {
        name: "stories",
        class: "__story",
        title: $t("View highlights"),
        condition: selectedIndex.value > -1 ? storyPublishers.value[selectedIndex.value].user.hasStories : false,
        event: () => showStories(storyPublishers.value[selectedIndex.value]),
      },
      {
        name: "profile",
        class: "__profile",
        title: $t("View profile"),
        condition:
          selectedIndex.value > -1
            ? !!storyPublishers.value[selectedIndex.value].user.publicLivechatUser ||
              storyPublishers.value[selectedIndex.value].user.hasStories
            : false,
        event: () => {
          root.$router.push(getUserUrl(storyPublishers.value[selectedIndex.value].user));
        },
      },
    ]);

    const getData = async () => {
      try {
        if (!config.featureStories) return;
        loading.value = true;
        await Promise.all([getStoryPublishers(), getAuthUserPublicData()]);
        addingMyUser();
      } finally {
        loading.value = false;
      }
    };
    getData();

    return {
      authUser,
      loading,
      showConfirm,
      selectedIndex,
      storyPublishers,
      enabledStories,
      buttons,
      getUserUrl,
      goLive,
      showStories,
      isMeAndCanCreateStory,
      selectUser,
    };
  },
});
